import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { formTemplatesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Trash, PencilSimple, ClipboardText, TextT, HashStraight,
  CalendarBlank, ListBullets, CheckSquare, RadioButton, ArrowUp, ArrowDown, Copy, Eye, FileArrowUp, Signature, TextAlignLeft
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';
import { getAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: TextT },
  { value: 'textarea', label: 'Long Text', icon: TextT },
  { value: 'number', label: 'Number', icon: HashStraight },
  { value: 'date', label: 'Date', icon: CalendarBlank },
  { value: 'select', label: 'Dropdown', icon: ListBullets },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio', label: 'Radio Buttons', icon: RadioButton },
  { value: 'signature', label: 'Signature', icon: Signature },
  { value: 'section', label: 'Heading/Paragraph', icon: TextAlignLeft },
];

const FormsPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Builder state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [appliesTo, setAppliesTo] = useState('both');
  const [fields, setFields] = useState([]);
  const [importingDocx, setImportingDocx] = useState(false);

  const handleDocxImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-selected
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please select a .docx file (save your .doc as .docx from Word first)');
      return;
    }
    setImportingDocx(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getAuthToken();
      const res = await axios.post(`${API_URL}/api/form-templates/extract-docx`, fd, {
        withCredentials: true,
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      const extracted = res.data?.fields || [];
      if (!extracted.length) { toast.error('No fields could be extracted'); return; }
      setFields(prev => [...prev, ...extracted]);
      if (!formName) {
        setFormName(file.name.replace(/\.docx$/i, '').replace(/[-_]+/g, ' '));
      }
      toast.success(`Imported ${extracted.length} field${extracted.length !== 1 ? 's' : ''} from ${file.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import document');
    } finally { setImportingDocx(false); }
  };

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await formTemplatesAPI.getAll();
      setTemplates(res.data);
    } catch (error) { toast.error('Failed to load forms'); }
    finally { setLoading(false); }
  };

  const resetBuilder = () => {
    setFormName(''); setFormDesc(''); setAppliesTo('both'); setFields([]); setEditingTemplate(null);
  };

  const openBuilder = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormName(template.name);
      setFormDesc(template.description || '');
      setAppliesTo(template.applies_to);
      setFields(template.fields.map(f => ({ ...f })));
    } else {
      resetBuilder();
    }
    setIsBuilderOpen(true);
  };

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      label: '',
      field_type: type,
      required: false,
      placeholder: '',
      options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : []
    };
    setFields([...fields, newField]);
  };

  const updateField = (index, key, value) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], [key]: value };
    setFields(updated);
  };

  const removeField = (index) => setFields(fields.filter((_, i) => i !== index));

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const target = index + direction;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFields(newFields);
  };

  const updateOption = (fieldIndex, optIndex, value) => {
    const updated = [...fields];
    const opts = [...(updated[fieldIndex].options || [])];
    opts[optIndex] = value;
    updated[fieldIndex] = { ...updated[fieldIndex], options: opts };
    setFields(updated);
  };

  const addOption = (fieldIndex) => {
    const updated = [...fields];
    const opts = [...(updated[fieldIndex].options || []), `Option ${(updated[fieldIndex].options?.length || 0) + 1}`];
    updated[fieldIndex] = { ...updated[fieldIndex], options: opts };
    setFields(updated);
  };

  const removeOption = (fieldIndex, optIndex) => {
    const updated = [...fields];
    updated[fieldIndex] = { ...updated[fieldIndex], options: updated[fieldIndex].options.filter((_, i) => i !== optIndex) };
    setFields(updated);
  };

  const handleSaveTemplate = async () => {
    if (!formName.trim()) { toast.error('Enter a form name'); return; }
    if (fields.length === 0) { toast.error('Add at least one field'); return; }
    if (fields.some(f => !f.label.trim())) { toast.error('All fields need a label'); return; }

    const data = { name: formName.trim(), description: formDesc.trim() || null, applies_to: appliesTo, fields };

    try {
      if (editingTemplate) {
        await formTemplatesAPI.update(editingTemplate.id, data);
        toast.success('Form template updated');
      } else {
        await formTemplatesAPI.create(data);
        toast.success('Form template created');
      }
      setIsBuilderOpen(false);
      resetBuilder();
      loadTemplates();
    } catch (error) { toast.error('Failed to save form template'); }
  };

  const handleDelete = async (id) => {
    try {
      await formTemplatesAPI.delete(id);
      toast.success('Template deleted');
      setTemplates(templates.filter(t => t.id !== id));
    } catch (error) { toast.error('Failed to delete template'); }
  };

  const getAppliesToBadge = (val) => {
    switch (val) {
      case 'client': return 'bg-green-100 text-green-800';
      case 'caregiver': return 'bg-blue-100 text-blue-800';
      default: return 'bg-purple-100 text-purple-800';
    }
  };

  if (user?.role !== 'supervisor') {
    return <div className="flex flex-col items-center justify-center h-64"><ClipboardText weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg">Access Restricted</h3></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="forms-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">Custom Forms</h1>
          <p className="text-muted-foreground mt-1">Build and manage reusable form templates</p>
        </div>
        <Button className="rounded-xl gap-2" onClick={() => openBuilder()} data-testid="create-form-btn">
          <Plus weight="bold" className="w-4 h-4" /> Create Form
        </Button>
      </div>

      {/* Form Builder Dialog */}
      <Dialog open={isBuilderOpen} onOpenChange={(open) => { if (!open) resetBuilder(); setIsBuilderOpen(open); }} modal={true}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingTemplate ? 'Edit Form Template' : 'Create Form Template'}</DialogTitle>
            <DialogDescription>Design your custom form with different field types</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Form Name *</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Client Intake Form" className="rounded-xl" data-testid="form-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Applies To</Label>
                <Select value={appliesTo} onValueChange={setAppliesTo}>
                  <SelectTrigger className="rounded-xl" data-testid="form-applies-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Clients Only</SelectItem>
                    <SelectItem value="caregiver">Caregivers Only</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Describe what this form is used for..." rows={2} className="rounded-xl" data-testid="form-desc-input" />
            </div>

            {/* Add Field Buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Add Fields</Label>
                <label className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-1.5 text-xs font-medium cursor-pointer hover:bg-muted transition-colors" data-testid="docx-import-label">
                  <FileArrowUp weight="duotone" className="w-4 h-4 text-primary" />
                  {importingDocx ? 'Importing...' : 'Upload .docx to auto-build'}
                  <input
                    type="file"
                    accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={handleDocxImport}
                    disabled={importingDocx}
                    data-testid="docx-import-input"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {FIELD_TYPES.map(ft => (
                  <Button key={ft.value} variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => addField(ft.value)} data-testid={`add-field-${ft.value}`}>
                    <ft.icon weight="duotone" className="w-4 h-4" /> {ft.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Fields List */}
            {fields.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-border rounded-2xl">
                <ClipboardText weight="duotone" className="w-12 h-12 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Click a field type above to start building</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((field, idx) => (
                  <Card key={field.id} className="rounded-xl border border-border" data-testid={`field-card-${idx}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="rounded-full text-xs capitalize">{field.field_type}</Badge>
                          <span className="text-xs text-muted-foreground">Field {idx + 1}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveField(idx, -1)} disabled={idx === 0}><ArrowUp className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1}><ArrowDown className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeField(idx)}><Trash className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Label *</Label>
                          <Input value={field.label} onChange={(e) => updateField(idx, 'label', e.target.value)} placeholder="Field label" className="rounded-lg text-sm" data-testid={`field-label-${idx}`} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Placeholder</Label>
                          <Input value={field.placeholder || ''} onChange={(e) => updateField(idx, 'placeholder', e.target.value)} placeholder="Placeholder text" className="rounded-lg text-sm" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={field.required} onCheckedChange={(v) => updateField(idx, 'required', v)} />
                        <Label className="text-xs">Required</Label>
                      </div>

                      {(field.field_type === 'select' || field.field_type === 'radio') && (
                        <div className="space-y-2 pl-2 border-l-2 border-border">
                          <Label className="text-xs">Options</Label>
                          {(field.options || []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <Input value={opt} onChange={(e) => updateOption(idx, oi, e.target.value)} className="rounded-lg text-sm flex-1" data-testid={`field-option-${idx}-${oi}`} />
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeOption(idx, oi)}><Trash className="w-3 h-3" /></Button>
                            </div>
                          ))}
                          <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => addOption(idx)}><Plus className="w-3 h-3" /> Add Option</Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setIsBuilderOpen(false); resetBuilder(); }}>Cancel</Button>
              <Button className="flex-1 rounded-xl" onClick={handleSaveTemplate} data-testid="save-form-template-btn">
                {editingTemplate ? 'Update Template' : 'Save Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={(open) => { if (!open) setPreviewTemplate(null); }} modal={true}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          {previewTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{previewTemplate.name}</DialogTitle>
                <DialogDescription>{previewTemplate.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {previewTemplate.fields.map((field, idx) => (
                  <div key={field.id} className="space-y-1">
                    <Label className="text-sm">{field.label} {field.required && <span className="text-destructive">*</span>}</Label>
                    {field.field_type === 'text' && <Input disabled placeholder={field.placeholder} className="rounded-xl" />}
                    {field.field_type === 'textarea' && <Textarea disabled placeholder={field.placeholder} className="rounded-xl" rows={3} />}
                    {field.field_type === 'number' && <Input type="number" disabled placeholder={field.placeholder} className="rounded-xl" />}
                    {field.field_type === 'date' && <Input type="date" disabled className="rounded-xl" />}
                    {field.field_type === 'select' && (
                      <Select disabled><SelectTrigger className="rounded-xl"><SelectValue placeholder="Select..." /></SelectTrigger></Select>
                    )}
                    {field.field_type === 'checkbox' && <div className="flex items-center gap-2"><Checkbox disabled /><span className="text-sm text-muted-foreground">{field.placeholder || field.label}</span></div>}
                    {field.field_type === 'radio' && (
                      <div className="space-y-2">{field.options?.map((opt, oi) => <div key={oi} className="flex items-center gap-2"><input type="radio" disabled /><span className="text-sm">{opt}</span></div>)}</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardText weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No form templates yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Create your first custom form</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(t => (
            <Card key={t.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`template-card-${t.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="font-heading text-lg">{t.name}</CardTitle>
                  <Badge className={`${getAppliesToBadge(t.applies_to)} rounded-full text-xs capitalize`}>{t.applies_to === 'both' ? 'All' : t.applies_to}</Badge>
                </div>
                {t.description && <CardDescription>{t.description}</CardDescription>}
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">{t.fields?.length || 0} fields</p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {t.fields?.slice(0, 4).map((f, i) => <Badge key={i} variant="secondary" className="text-[10px] rounded-full capitalize">{f.field_type}</Badge>)}
                  {t.fields?.length > 4 && <Badge variant="secondary" className="text-[10px] rounded-full">+{t.fields.length - 4}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-xl gap-1 flex-1" onClick={() => setPreviewTemplate(t)} data-testid={`preview-template-${t.id}`}>
                    <Eye weight="bold" className="w-3 h-3" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl gap-1 flex-1" onClick={() => openBuilder(t)} data-testid={`edit-template-${t.id}`}>
                    <PencilSimple weight="bold" className="w-3 h-3" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-xl text-destructive" onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash weight="bold" className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Created by {t.created_by_name} &middot; {format(new Date(t.created_at), 'MMM d, yyyy')}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormsPage;
